import { PartnerSearch } from './serviceRole/PartnerSearch';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { getServiceRoleStepMetadata } from 'utils/json-form/formDataUtils';
import { createServiceRole, deleteServiceRole } from 'thunks/serviceRoleThunk';
import { LoadingSpinner } from 'components/loadingSpinner/LoadingSpinner';
import { jbColors } from 'theme/jbColors';
import {
  PartnerSearchResponse,
  ServiceRole as ServiceRoleType,
  ServiceRoleDefinition,
} from 'pco-product-service-internal-consumer';
import { ServiceRole } from './serviceRole/ServiceRole';
import { toUtcCalendarDate } from 'utils/dateUtils';

type Inputs = {
  validFrom?: Date;
  validTo?: Date;
  justification?: string | undefined;
};

type ServiceRoleFormWithPartnerSearchProps = {
  isReadOnly?: boolean;
  //TODO: refactor these props and the corresponding logic
  setLocalServiceRole?: Dispatch<SetStateAction<ServiceRoleType | null>>;
  setLocalPresentServiceRole?: Dispatch<SetStateAction<ServiceRoleType | null>>;
  serviceRole?: ServiceRoleType;
  setRoleModified?: Dispatch<SetStateAction<boolean>>;
  setServiceRoleCreated?: Dispatch<SetStateAction<boolean>>;
  setAdditionalDelegateModalOpen?: Dispatch<SetStateAction<boolean>>;
};

const ServiceRoleFormWithPartnerSearch = ({
  isReadOnly,
  setLocalServiceRole,
  setLocalPresentServiceRole,
  serviceRole,
  setServiceRoleCreated,
  setRoleModified,
  setAdditionalDelegateModalOpen,
}: ServiceRoleFormWithPartnerSearchProps) => {
  const [validationErrorMessage] = useState<string>('');
  const [partnerForServiceRole, setPartnerForServiceRole] = useState<PartnerSearchResponse | null>(null);
  const openTaskUid = useSelector((state: RootState) => state.serviceAgreements.openedTaskUid);

  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);

  const isDisabledBecauseIsCompletedOpenTask =
    selectedServiceAgreement?.tasks?.find((task) => task.taskUid === openTaskUid)?.resolution === 'Completed';

  const { loader } = useSelector((state: RootState) => state.appConfig);
  const submitButtonTitle =
    selectedServiceAgreement?.serviceDefinitionName === 'ID Delegation'
      ? 'Add Delegate'
      : serviceRole
        ? 'Modify Service Role'
        : 'Add Service Role';

  const dispatch = useDispatch<AppDispatch>();

  const serviceRoleDefinition = useMemo<ServiceRoleDefinition | undefined>(() => {
    if (!selectedServiceAgreement) return undefined;
    const serviceRoleStepMetadata = getServiceRoleStepMetadata(selectedServiceAgreement);
    return serviceRoleStepMetadata?.serviceRoleList?.[0];
  }, [selectedServiceAgreement]);

  const [inputs, setInputs] = useState<Inputs>({
    validFrom: serviceRole?.validFrom,
    validTo: serviceRole?.validTo,
    justification: serviceRole?.justification ?? '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (setRoleModified) {
      setRoleModified(true);
    }

    setInputs((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const handleChangeDatePicker = (e: dayjs.Dayjs | React.SyntheticEvent | null, key: string) => {
    if (setRoleModified) {
      setRoleModified(true);
    }

    if (!e) {
      setInputs((prevState) => ({
        ...prevState,
        [key]: undefined,
      }));
      return;
    }
    const val = toUtcCalendarDate(dayjs(e as dayjs.Dayjs).toDate());
    setInputs((prevState) => ({
      ...prevState,
      [key]: val,
    }));
  };

  useEffect(() => {
    if (setLocalServiceRole) {
      let presentServiceRole;
      // hack for ID Delegatio

      if (
        selectedServiceAgreement?.serviceDefinitionName !== 'ID Delegation' &&
        selectedServiceAgreement?.serviceRoles &&
        selectedServiceAgreement?.serviceRoles?.length > 0
      ) {
        presentServiceRole = selectedServiceAgreement?.serviceRoles[0];
      }

      const serviceRole = {
        validFrom: inputs?.validFrom,
        validTo: inputs?.validTo,
        justification: inputs?.justification,
        serviceAgreementUid: selectedServiceAgreement?.serviceAgreementId,
        serviceRoleDefinition: serviceRoleDefinition,
        partnerUid: partnerForServiceRole?.techId,
        domicileCountry: partnerForServiceRole?.mainDomicileCountry,
        partnerNumber: partnerForServiceRole?.globalPartnerId,
        fullName: partnerForServiceRole?.fullName,
      };

      setLocalServiceRole(serviceRole as ServiceRoleType);
      if (selectedServiceAgreement?.serviceDefinitionName !== 'ID Delegation' && setLocalPresentServiceRole) {
        setLocalPresentServiceRole(presentServiceRole ?? null);
      }
    }
  }, [
    inputs,
    partnerForServiceRole,
    selectedServiceAgreement?.serviceAgreementId,
    selectedServiceAgreement?.serviceDefinitionName,
    selectedServiceAgreement?.serviceRoles,
    serviceRoleDefinition,
    setLocalPresentServiceRole,
    setLocalServiceRole,
  ]);

  const handleCreateServiceRole = () => {
    const tempServiceRole = {
      validFrom: inputs?.validFrom,
      validTo: inputs?.validTo,
      justification: inputs?.justification,
      serviceAgreementUid: selectedServiceAgreement?.serviceAgreementId,
      serviceRoleDefinition: serviceRoleDefinition,
      partnerUid: partnerForServiceRole?.techId,
      domicileCountry: partnerForServiceRole?.mainDomicileCountry,
      partnerNumber: partnerForServiceRole?.globalPartnerId,
      fullName: partnerForServiceRole?.fullName,
    };

    if (selectedServiceAgreement?.serviceDefinitionName === 'ID Delegation') {
      dispatch(createServiceRole(tempServiceRole, undefined, setAdditionalDelegateModalOpen));
    } else {
      dispatch(createServiceRole(tempServiceRole, serviceRole?.serviceRoleUid));
      if (setServiceRoleCreated) {
        setServiceRoleCreated(true);
      }
      if (setRoleModified) {
        setRoleModified(true);
      }
    }
  };

  const handleDeleteServiceRole = () => {
    if (serviceRole?.serviceRoleUid) {
      dispatch(deleteServiceRole(serviceRole.serviceRoleUid));
    }
  };

  const hasValidationError = !inputs.validFrom || !partnerForServiceRole;

  return loader.loading ? (
    <LoadingSpinner message={loader.message} />
  ) : (
    <Box sx={{ ml: '-1em' }}>
      <PartnerSearch
        handleChangeValidFrom={handleChangeDatePicker}
        handleChangeValidTo={handleChangeDatePicker}
        setPartnerForServiceRole={setPartnerForServiceRole}
        partnerForServiceRole={partnerForServiceRole}
        setRoleModified={setRoleModified}
        serviceRole={serviceRole}
        disabled={isReadOnly || isDisabledBecauseIsCompletedOpenTask}
      />

      <ServiceRole
        inputs={inputs}
        serviceRole={serviceRole}
        handleChangeValidFrom={handleChangeDatePicker}
        handleChangeValidTo={handleChangeDatePicker}
        handleChange={handleInputChange}
        disabled={isReadOnly || isDisabledBecauseIsCompletedOpenTask}
        error={hasValidationError}
        serviceRoleDefinition={serviceRoleDefinition}
      />

      {!isReadOnly ? (
        <>
          <Button
            disabled={hasValidationError}
            data-testid="add-service-role-button"
            sx={{
              color: jbColors.core.reflexBlue[100],
              ml: 2.2,
              backgroundColor: 'white',
              ':hover': {
                backgroundColor: jbColors.core.reflexBlue[100],
                color: 'white',
              },
            }}
            onClick={handleCreateServiceRole}
          >
            {submitButtonTitle}
          </Button>

          <Typography data-testid="validation-error-message" variant="body1" sx={{ mt: 2, ml: 2, color: 'red' }}>
            {validationErrorMessage}
          </Typography>
        </>
      ) : (
        <Button sx={{ mb: 6, ml: 2 }} onClick={handleDeleteServiceRole}>
          Remove Delegate
        </Button>
      )}
    </Box>
  );
};

export default ServiceRoleFormWithPartnerSearch;
